<template>
  <div>

    <!-- Page Header -->
    <CCard class="mb-4 border-0 shadow-sm">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="6">
            <h1 class="h4 mb-1 font-weight-bold">
              {{ obj.id ? `Payment voucher ${obj.voucherNumber || ""}`.trim() : "New payment voucher" }}
              <CBadge
                v-if="obj.id"
                :color="badgeColor('paymentVoucher', obj.statusDescription || 'Draft')"
                class="ml-2 align-middle"
              >
                {{ badgeLabel('paymentVoucher', obj.statusDescription || 'Draft') }}
              </CBadge>
            </h1>
            <p class="text-muted mb-0">
              {{
                obj.id
                  ? (selectedVendor && selectedVendor.name) || obj.payeeName || "Vendor payment"
                  : "Get a payment to a vendor approved and recorded"
              }}
            </p>
          </CCol>
          <CCol md="6" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CDropdown
              placement="bottom-end"
              toggler-text="Actions"
              color="light"
            >
              <CDropdownItem @click="addNew()">New voucher</CDropdownItem>
              <template v-if="obj.id && otherStatuses.length">
                <CDropdownDivider />
                <CDropdownItem
                  v-for="status in otherStatuses"
                  :key="status.id"
                  @click="askStatusChange(status)"
                >{{ statusActionLabel(status) }}</CDropdownItem>
              </template>
            </CDropdown>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <!-- Main Form -->
    <CRow>
      <CCol lg="12">
        <CCard class="mb-4 border-0 shadow-sm">
          <CCardHeader class="bg-transparent border-0 p-4">
            <h5 class="mb-0">Voucher details</h5>
          </CCardHeader>
          <CCardBody class="p-4">
            <CRow>
              <!-- Left Column -->
              <CCol md="6">
                <div class="form-group" ref="vendorField">
                  <label for="pv-vendor">Vendor *</label>
                  <v-select
                    input-id="pv-vendor"
                    style="width: 100%"
                    v-model="selectedVendor"
                    :label="'name'"
                    :options="vendorItemsWithAddNew"
                    placeholder="Select vendor"
                    @input="handleVendorSelect"
                  >
                    <span slot="no-options">
                      <CButton color="link" class="p-0" @click="addNewVendor">Add new vendor</CButton>
                    </span>
                  </v-select>
                  <div v-if="errors.vendor" class="invalid-feedback d-block">{{ errors.vendor }}</div>
                </div>

                <div
                  v-if="selectedVendor && selectedVendor.id !== 'add_new'"
                  class="vendor-details p-3 bg-light rounded mb-3"
                >
                  <div class="d-flex align-items-center mb-2">
                    <div
                      class="c-avatar c-avatar-sm bg-primary text-white mr-2"
                    >
                      {{ selectedVendor.name ? selectedVendor.name.charAt(0) : "?" }}
                    </div>
                    <div class="font-weight-bold">
                      {{ selectedVendor.name }}
                    </div>
                  </div>
                  <div class="small text-muted mb-1" v-if="selectedVendor.address">
                    <CIcon name="cil-location-pin" />
                    {{ selectedVendor.address }}
                  </div>
                  <div class="small text-muted mb-1" v-if="selectedVendor.contactEmail">
                    <CIcon name="cil-envelope-closed" />
                    {{ selectedVendor.contactEmail }}
                  </div>
                  <div class="small text-muted" v-if="selectedVendor.contactPhone">
                    <CIcon name="cil-phone" />
                    {{ selectedVendor.contactPhone }}
                  </div>
                </div>

                <CInput
                  label="Payee name"
                  placeholder="Name of payee (if different from vendor)"
                  v-model="obj.payeeName"
                />

                <CTextarea
                  label="Description"
                  placeholder="Payment description"
                  v-model="obj.description"
                  rows="3"
                />
              </CCol>

              <!-- Right Column -->
              <CCol md="6">
                <div class="form-group">
                  <label>Voucher number</label>
                  <div class="py-2 text-monospace">{{ obj.voucherNumber || "Assigned when you save" }}</div>
                </div>

                <CRow>
                  <CCol sm="6">
                    <CInput
                      ref="voucherDateField"
                      label="Voucher date *"
                      type="date"
                      :value="computeVoucherDate"
                      :is-valid="errors.voucherDate ? false : null"
                      :invalid-feedback="errors.voucherDate"
                      @change="setVoucherDate"
                    />
                  </CCol>
                  <CCol sm="6">
                    <CInput
                      label="Payment date"
                      type="date"
                      :value="computePaymentDate"
                      @change="setPaymentDate"
                    />
                  </CCol>
                </CRow>

                <CSelect
                  ref="methodField"
                  label="Payment method *"
                  :value.sync="obj.paymentMethod"
                  :is-valid="errors.paymentMethod ? false : null"
                  :invalid-feedback="errors.paymentMethod"
                  :options="[
                    { value: '', label: 'Select payment method' },
                    { value: 'Bank Transfer', label: 'Bank transfer' },
                    { value: 'Check', label: 'Cheque' },
                    { value: 'Cash', label: 'Cash' },
                    { value: 'Online Payment', label: 'Online payment' },
                    { value: 'Credit Card', label: 'Credit card' }
                  ]"
                />

                <CInput
                  label="Payment reference"
                  placeholder="Cheque no. or transaction ID"
                  v-model="obj.paymentReference"
                />

                <CInput
                  ref="amountField"
                  label="Total amount *"
                  type="number"
                  inputmode="decimal"
                  step="0.01"
                  prepend="RM"
                  v-model.number="obj.totalAmount"
                  placeholder="0.00"
                  :is-valid="errors.totalAmount ? false : null"
                  :invalid-feedback="errors.totalAmount"
                />
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        <!-- Payment Items Section -->
        <CCard class="mb-4 border-0 shadow-sm">
          <CCardHeader class="bg-transparent border-0 p-4">
            <CRow class="align-items-center">
              <CCol>
                <h5 class="mb-0">Payment items</h5>
                <div v-if="!selectedVendor" class="small text-muted">Choose a vendor to add their unpaid bills or expenses.</div>
              </CCol>
              <CCol class="d-flex justify-content-end">
                <CButton
                  color="primary"
                  variant="outline"
                  size="sm"
                  class="mr-2"
                  @click="showUnpaidBillsModal = true"
                  :disabled="!selectedVendor"
                >
                  <CIcon name="cil-file" /> Add bills
                </CButton>
                <CButton
                  color="primary"
                  variant="outline"
                  size="sm"
                  @click="showUnpaidExpensesModal = true"
                  :disabled="!selectedVendor"
                >
                  <CIcon name="cil-plus" /> Add expenses
                </CButton>
              </CCol>
            </CRow>
          </CCardHeader>
          <CCardBody class="p-4">
            <CDataTable
              :items="obj.items || []"
              :fields="itemFields"
              hover
              small
            >
              <template #itemType="{ item }">
                <td>
                  <CBadge :color="getItemTypeColor(item.itemType)">
                    {{ getItemTypeLabel(item.itemType) }}
                  </CBadge>
                </td>
              </template>

              <template #referenceNumber="{ item }">
                <td>
                  <div class="font-weight-bold">{{ item.referenceNumber }}</div>
                  <div class="small text-muted">{{ item.description }}</div>
                </td>
              </template>

              <template #originalAmount="{ item }">
                <td class="text-right">{{ formatCurrency(item.originalAmount) }}</td>
              </template>

              <template #previouslyPaid="{ item }">
                <td class="text-right">{{ formatCurrency(item.previouslyPaid) }}</td>
              </template>

              <template #amount="{ item }">
                <td class="text-right">
                  <CInput
                    type="number"
                    inputmode="decimal"
                    step="0.01"
                    aria-label="Amount paying now (RM)"
                    v-model.number="item.amount"
                    @input="calculateTotal"
                    style="max-width: 120px"
                  />
                </td>
              </template>

              <template #remainingBalance="{ item }">
                <td class="text-right">
                  {{ formatCurrency(item.remainingBalance) }}
                </td>
              </template>

              <template #actions="{ item, index }">
                <td class="text-center">
                  <CButton
                    color="danger"
                    size="sm"
                    @click="removeItem(index)"
                    variant="ghost"
                    aria-label="Remove item"
                    title="Remove item"
                  >
                    <CIcon name="cil-trash" />
                  </CButton>
                </td>
              </template>
            </CDataTable>

            <CRow class="mt-4">
              <CCol class="text-right">
                <h5>
                  Total payment
                  <strong class="ml-2">{{ formatCurrency(itemsTotal) }}</strong>
                </h5>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        <!-- Payment Slip Upload Section -->
        <CCard class="mb-4 border-0 shadow-sm" v-if="obj.id">
          <CCardHeader class="bg-transparent border-0 p-4">
            <h5 class="mb-0">
              <CIcon name="cil-paperclip" class="mr-2" />
              Payment slip
            </h5>
          </CCardHeader>
          <CCardBody class="p-4">
            <!-- Document Display -->
            <div v-if="obj.documentPaymentProofId && obj.documentPaymentProof">
              <div class="form-group mb-4">
                <label class="form-label">Preview</label>

                <!-- PDF Display -->
                <div v-if="isPdf(obj.documentPaymentProof)" class="border rounded">
                  <iframe
                    :src="getPaymentSlipUrl()"
                    width="100%"
                    height="600"
                    style="border: none;"
                  ></iframe>
                </div>

                <!-- Image Display -->
                <div v-else class="border rounded">
                  <CImg
                    :src="getPaymentSlipUrl()"
                    width="100%"
                    height="600"
                    responsive
                    style="object-fit: contain;"
                  />
                </div>

                <!-- Document Actions -->
                <div class="mt-3 d-flex justify-content-between align-items-center">
                  <div>
                    <a
                      :href="getPaymentSlipUrl()"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-primary mr-3"
                    >
                      <CIcon name="cil-external-link" class="mr-1" />
                      Open in new tab
                    </a>
                    <span class="text-muted">
                      <CIcon name="cil-description" class="mr-1" />
                      {{ getPaymentSlipName() }}
                    </span>
                  </div>
                  <CButton
                    color="danger"
                    size="sm"
                    @click="showRemoveSlipModal = true"
                    variant="outline"
                  >
                    <CIcon name="cil-trash" /> Remove
                  </CButton>
                </div>
              </div>
            </div>

            <!-- Upload New Document -->
            <CRow form>
              <CCol md="6">
                <div class="form-group">
                  <label class="form-label">
                    {{ obj.documentPaymentProofId ? 'Replace payment slip' : 'Upload payment slip' }}
                  </label>
                  <WidgetsUploadDocument @uploaded="uploadedPaymentSlip" />
                </div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        <!-- Action Buttons -->
        <CCard class="mb-4 border-0 shadow-sm">
          <CCardBody class="p-4">
            <CRow>
              <CCol class="d-flex justify-content-between">
                <CButton color="light" :disabled="saving" @click="cancel">
                  Cancel
                </CButton>
                <div>
                  <CButton
                    :color="!obj.id || obj.status === 0 ? 'light' : 'primary'"
                    :disabled="saving"
                    @click="save()"
                    class="mr-2"
                  >
                    {{ obj.id ? "Save" : "Create voucher" }}
                  </CButton>
                  <CButton
                    color="primary"
                    :disabled="saving"
                    @click="saveAndApprove"
                    v-if="!obj.id || obj.status === 0"
                  >
                    <CSpinner v-if="saving" size="sm" class="mr-1" />
                    Save and submit for approval
                  </CButton>
                </div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- Preview Section -->
    <CRow v-if="obj.id">
      <CCol lg="12">
        <CCard class="border-0 shadow-sm">
          <CCardHeader class="bg-transparent border-0 p-4">
            <h5 class="mb-0">Preview</h5>
          </CCardHeader>
          <CCardBody class="p-4">
            <WidgetsReportPaymentVoucher
              v-if="computedPreviewDocument"
              :document="computedPreviewDocument"
            />
            <div v-else class="text-center text-muted p-5">
              <p>Save the payment voucher to see the preview</p>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- Unpaid Bills Modal -->
    <CModal
      :show.sync="showUnpaidBillsModal"
      title="Select bills to pay"
    >
      <p v-if="!unpaidBills.length" class="text-muted mb-0">This vendor has no unpaid bills.</p>
      <CDataTable
        v-else
        :items="unpaidBills"
        :fields="billFields"
        hover
        small
      >
        <template #select="{ item }">
          <td class="text-center">
            <CInputCheckbox
              :checked="isItemSelected('bill', item.id)"
              :custom="false"
              :aria-label="'Select bill ' + (item.billNo || '')"
              @update:checked="toggleBillSelection(item)"
            />
          </td>
        </template>

        <template #billNo="{ item }">
          <td>
            <div class="font-weight-bold">{{ item.billNo }}</div>
            <div class="small text-muted">
              {{ formatDate(item.date) }}
            </div>
          </td>
        </template>

        <template #totalAmount="{ item }">
          <td class="text-right">{{ formatCurrency(item.totalAmount) }}</td>
        </template>
      </CDataTable>

      <template #footer>
        <CButton color="light" @click="showUnpaidBillsModal = false">
          Cancel
        </CButton>
        <CButton color="primary" :disabled="!selectedBills.length" @click="addSelectedBills">
          Add {{ selectedBills.length || "" }} bill{{ selectedBills.length === 1 ? "" : "s" }}
        </CButton>
      </template>
    </CModal>

    <!-- Unpaid Expenses Modal -->
    <CModal
      :show.sync="showUnpaidExpensesModal"
      title="Select expenses to pay"
    >
      <p v-if="!unpaidExpenses.length" class="text-muted mb-0">This vendor has no unpaid expenses.</p>
      <CDataTable
        v-else
        :items="unpaidExpenses"
        :fields="expenseFields"
        hover
        small
      >
        <template #select="{ item }">
          <td class="text-center">
            <CInputCheckbox
              :checked="isItemSelected('expense', item.id)"
              :aria-label="'Select expense ' + (item.name || '')"
              @update:checked="toggleExpenseSelection(item)"
            />
          </td>
        </template>

        <template #name="{ item }">
          <td>
            <div class="font-weight-bold">{{ item.name }}</div>
            <div class="small text-muted">
              {{ formatDate(item.date) }}
            </div>
          </td>
        </template>

        <template #totalAmount="{ item }">
          <td class="text-right">{{ formatCurrency(item.totalAmount) }}</td>
        </template>
      </CDataTable>

      <template #footer>
        <CButton color="light" @click="showUnpaidExpensesModal = false">
          Cancel
        </CButton>
        <CButton color="primary" :disabled="!selectedExpenses.length" @click="addSelectedExpenses">
          Add {{ selectedExpenses.length || "" }} expense{{ selectedExpenses.length === 1 ? "" : "s" }}
        </CButton>
      </template>
    </CModal>

    <!-- Add New Vendor Modal (shared) -->
    <VendorFormModal
      :show="addVendorFormPopup"
      :vendor="itemAddNewVendor"
      @close="addVendorFormPopup = false"
      @save="saveNewVendor"
    />

    <!-- Remove payment slip confirmation -->
    <CModal title="Remove the payment slip?" :show.sync="showRemoveSlipModal">
      The uploaded payment slip will be removed from voucher {{ obj.voucherNumber }}. The voucher itself stays as it is.
      <template #footer>
        <CButton color="light" @click="showRemoveSlipModal = false">Keep it</CButton>
        <CButton color="danger" @click="removePaymentSlip">Remove slip</CButton>
      </template>
    </CModal>

    <!-- Status change confirmation -->
    <CModal
      :title="statusConfirmTitle"
      :show.sync="statusConfirm.show"
      :close-on-backdrop="!statusConfirm.busy"
    >
      <p class="mb-0">{{ statusConfirmMessage }}</p>
      <template #footer>
        <CButton color="light" :disabled="statusConfirm.busy" @click="statusConfirm.show = false">Cancel</CButton>
        <CButton
          :color="statusConfirmDanger ? 'danger' : 'primary'"
          :disabled="statusConfirm.busy"
          @click="confirmStatusChange"
        >
          <CSpinner v-if="statusConfirm.busy" size="sm" class="mr-1" />
          {{ statusConfirm.status ? statusActionLabel(statusConfirm.status) : "" }}
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import paymentVoucherApi from "@/lib/paymentVoucherApi";
import VendorApi from "@/lib/vendorApi";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import WidgetsReportPaymentVoucher from "@/views/widgets/WidgetsReportPaymentVoucher.vue";
import WidgetsUploadDocument from "@/views/widgets/WidgetsUploadDocument.vue";
import VendorFormModal from "@/views/components/VendorFormModal.vue";
import { formatMyr } from "@/utils/money";
import { formatDate, mytToday, parseDateInput } from "@/utils/datetime";
import { badgeColor, badgeLabel } from "@/utils/statusBadges";

export default {
  name: "PaymentVoucher",
  components: {
    vSelect,
    WidgetsReportPaymentVoucher,
    WidgetsUploadDocument,
    VendorFormModal,
  },
  data() {
    return {
      saving: false,
      errors: { vendor: "", voucherDate: "", paymentMethod: "", totalAmount: "" },
      showRemoveSlipModal: false,
      statusConfirm: { show: false, status: null, busy: false },
      obj: {
        id: null,
        voucherNumber: "",
        voucherDate: mytToday(),
        paymentDate: mytToday(),
        vendorId: null,
        payeeName: "",
        paymentMethod: "Bank Transfer",
        paymentReference: "",
        totalAmount: 0,
        description: "",
        notes: "",
        status: 0,
        statusDescription: "Draft",
        items: [],
      },
      selectedVendor: null,
      vendorItems: [],
      unpaidBills: [],
      unpaidExpenses: [],
      selectedBills: [],
      selectedExpenses: [],
      showUnpaidBillsModal: false,
      showUnpaidExpensesModal: false,
      statuses: [],
      addVendorFormPopup: false,
      itemAddNewVendor: {},
      itemFields: [
        {
          key: "itemType",
          label: "Type",
          _style: "width: 10%;",
        },
        {
          key: "referenceNumber",
          label: "Reference",
          _style: "width: 20%;",
        },
        {
          key: "originalAmount",
          label: "Original",
          _style: "width: 12%;",
        },
        {
          key: "previouslyPaid",
          label: "Paid",
          _style: "width: 12%;",
        },
        {
          key: "amount",
          label: "Paying now",
          _style: "width: 15%;",
        },
        {
          key: "remainingBalance",
          label: "Balance",
          _style: "width: 12%;",
        },
        {
          key: "actions",
          label: "",
          _style: "width: 5%;",
        },
      ],
      billFields: [
        { key: "select", label: "", _style: "width: 5%;" },
        { key: "billNo", label: "Bill no.", _style: "width: 25%;" },
        { key: "totalAmount", label: "Amount", _style: "width: 20%;" },
        { key: "companyName", label: "Description", _style: "width: 50%;" },
      ],
      expenseFields: [
        { key: "select", label: "", _style: "width: 5%;" },
        { key: "name", label: "Expense", _style: "width: 40%;" },
        { key: "totalAmount", label: "Amount", _style: "width: 20%;" },
        { key: "expenseType", label: "Type", _style: "width: 35%;" },
      ],
    };
  },
  computed: {
    vendorItemsWithAddNew() {
      return [
        ...this.vendorItems,
        { id: "add_new", name: "+ Add new vendor" },
      ];
    },
    otherStatuses() {
      return (this.statuses || []).filter((s) => s.name !== this.obj.statusDescription);
    },
    statusConfirmDanger() {
      const name = this.statusConfirm.status && this.statusConfirm.status.name;
      return name === "Rejected" || name === "Voided";
    },
    statusConfirmTitle() {
      const s = this.statusConfirm.status;
      if (!s) return "";
      const no = this.obj.voucherNumber ? ` ${this.obj.voucherNumber}` : "";
      switch (s.name) {
        case "Draft": return `Move payment voucher${no} back to draft?`;
        case "PendingApproval": return `Submit payment voucher${no} for approval?`;
        case "Approved": return `Approve payment voucher${no}?`;
        case "Paid": return `Mark payment voucher${no} as paid?`;
        case "Reconciled": return `Mark payment voucher${no} as reconciled?`;
        case "Rejected": return `Reject payment voucher${no}?`;
        case "Voided": return `Void payment voucher${no}?`;
        default: return `Change payment voucher${no}?`;
      }
    },
    statusConfirmMessage() {
      const s = this.statusConfirm.status;
      if (!s) return "";
      const amount = this.formatCurrency(this.obj.totalAmount);
      const payee = (this.selectedVendor && this.selectedVendor.name) || this.obj.payeeName || "the payee";
      switch (s.name) {
        case "PendingApproval": return `The ${amount} payment to ${payee} will be sent for approval.`;
        case "Approved": return `The ${amount} payment to ${payee} will be approved and can then be paid.`;
        case "Paid": return `The ${amount} payment to ${payee} will be recorded as paid in your accounts.`;
        case "Reconciled": return `The ${amount} payment to ${payee} will be marked as matched with your bank statement.`;
        case "Rejected": return `The ${amount} payment to ${payee} will be rejected and won't be paid.`;
        case "Voided": return `The ${amount} payment to ${payee} will be voided. This can't be undone.`;
        case "Draft": return `The voucher goes back to draft so you can change it.`;
        default: return `The voucher will be changed to ${badgeLabel("paymentVoucher", s.name).toLowerCase()}.`;
      }
    },
    computeVoucherDate() {
      if (!this.obj.voucherDate) return "";
      return this.obj.voucherDate.substring(0, 10);
    },
    computePaymentDate() {
      if (!this.obj.paymentDate) return "";
      return this.obj.paymentDate.substring(0, 10);
    },
    itemsTotal() {
      if (!this.obj.items || this.obj.items.length === 0) return 0;
      return this.obj.items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    },
    computedPreviewDocument() {
      if (!this.obj.id || !this.obj.vendorId) return null;

      return {
        ...this.obj,
        vendor: this.selectedVendor,
        business: window.helper?.currentBusiness || {},
        items: this.obj.items || [],
      };
    },
  },
  async mounted() {
    await this.loadVendors();
    await this.loadStatuses();

    if (this.$route.params.id) {
      await this.loadVoucher(this.$route.params.id);
    } else {
      await this.generateVoucherNumber();
    }
  },
  methods: {
    async loadVendors() {
      try {
        const vendorApi = new VendorApi();
        const data = await vendorApi.getListByCurrentBusiness();
        this.vendorItems = data.result;
      } catch (error) {
        console.error("Failed to load vendors:", error);
        this.$toast.error("Couldn't load your vendors. Refresh the page to try again.");
      }
    },
    async loadStatuses() {
      try {
        const data = await paymentVoucherApi.getPaymentVoucherStatus();
        this.statuses = data.result;
      } catch (error) {
        console.error("Failed to load statuses", error);
      }
    },
    async loadVoucher(id) {
      try {
        const data = await paymentVoucherApi.get(id);
        this.obj = data.result;

        if (this.obj.vendorId && this.vendorItems.length > 0) {
          this.selectedVendor = this.vendorItems.find(
            (v) => v.id === this.obj.vendorId
          );
        }
      } catch (error) {
        this.$toast.error("Couldn't load the payment voucher. Check your connection and try again.");
      }
    },
    async generateVoucherNumber() {
      try {
        const data = await paymentVoucherApi.getNextNumber();
        this.obj.voucherNumber = data.result;
      } catch (error) {
        console.error("Failed to generate voucher number", error);
      }
    },
    handleVendorSelect(vendor) {
      if (vendor && vendor.id === "add_new") {
        this.addNewVendor();
        this.selectedVendor = null;
      } else if (vendor) {
        this.errors.vendor = "";
        this.obj.vendorId = vendor.id;
        this.obj.payeeName = vendor.name;
        this.loadUnpaidBills();
        this.loadUnpaidExpenses();
      } else {
        this.obj.vendorId = null;
        this.obj.payeeName = "";
      }
    },
    addNewVendor() {
      this.itemAddNewVendor = {};
      this.addVendorFormPopup = true;
    },
    async saveNewVendor(vendor) {
      try {
        const vendorApi = new VendorApi();
        const created = await vendorApi.create(vendor || this.itemAddNewVendor);
        const newVendor = (created && created.result) || created;

        // Reload vendors list
        await this.loadVendors();

        // Select the newly created vendor
        this.selectedVendor = this.vendorItems.find(v => v.id === newVendor.id);
        this.obj.vendorId = newVendor.id;
        this.obj.payeeName = newVendor.name;

        // Close modal
        this.errors.vendor = "";
        this.addVendorFormPopup = false;
        this.itemAddNewVendor = {};
        this.$toast.success("Vendor added");
      } catch (error) {
        this.$toast.error("Couldn't add the vendor. " + helper.getErrorMessage(error && error.data, "Please try again."));
      }
    },
    async loadUnpaidBills() {
      if (!this.obj.vendorId) return;

      try {
        const data = await paymentVoucherApi.getUnpaidBills(this.obj.vendorId);
        this.unpaidBills = data.result;
      } catch (error) {
        this.$toast.error("Couldn't load this vendor's unpaid bills. Please try again.");
      }
    },
    async loadUnpaidExpenses() {
      if (!this.obj.vendorId) return;

      try {
        const data = await paymentVoucherApi.getUnpaidExpenses(this.obj.vendorId);
        this.unpaidExpenses = data.result || data;
      } catch (error) {
        this.$toast.error("Couldn't load this vendor's unpaid expenses. Please try again.");
      }
    },
    toggleBillSelection(bill) {
      const index = this.selectedBills.findIndex((b) => b.id === bill.id);
      if (index >= 0) {
        this.selectedBills.splice(index, 1);
      } else {
        this.selectedBills.push(bill);
      }
    },
    toggleExpenseSelection(expense) {
      const index = this.selectedExpenses.findIndex((e) => e.id === expense.id);
      if (index >= 0) {
        this.selectedExpenses.splice(index, 1);
      } else {
        this.selectedExpenses.push(expense);
      }
    },
    isItemSelected(type, id) {
      if (type === "bill") {
        return this.selectedBills.some((b) => b.id === id);
      } else {
        return this.selectedExpenses.some((e) => e.id === id);
      }
    },
    addSelectedBills() {
      this.selectedBills.forEach((bill) => {
        this.obj.items.push({
          id: this.generateGuid(),
          itemType: 0, // Bill
          billId: bill.id,
          referenceNumber: bill.billNo,
          description: bill.companyName || bill.description,
          originalAmount: bill.totalAmount,
          previouslyPaid: 0,
          amount: bill.totalAmount,
          remainingBalance: 0,
        });
      });

      this.calculateTotal();
      this.selectedBills = [];
      this.showUnpaidBillsModal = false;
    },
    addSelectedExpenses() {
      this.selectedExpenses.forEach((expense) => {
        this.obj.items.push({
          id: this.generateGuid(),
          itemType: 1, // Expense
          expenseId: expense.id,
          referenceNumber: expense.name,
          description: expense.description || expense.expenseType,
          originalAmount: expense.totalAmount,
          previouslyPaid: 0,
          amount: expense.totalAmount,
          remainingBalance: 0,
        });
      });

      this.calculateTotal();
      this.selectedExpenses = [];
      this.showUnpaidExpensesModal = false;
    },
    removeItem(index) {
      this.obj.items.splice(index, 1);
      this.calculateTotal();
    },
    calculateTotal() {
      // Calculate remaining balance for each item
      this.obj.items.forEach((item) => {
        item.remainingBalance =
          parseFloat(item.originalAmount) -
          parseFloat(item.previouslyPaid) -
          parseFloat(item.amount);
      });

      // Update total amount
      this.obj.totalAmount = this.itemsTotal;
    },
    // CInput emits the value; accept a native event too. Keep wall-clock dates as "yyyy-MM-dd".
    setVoucherDate(e) {
      const value = e && e.target ? e.target.value : e;
      this.obj.voucherDate = parseDateInput(value) || null;
      if (this.obj.voucherDate) this.errors.voucherDate = "";
    },
    setPaymentDate(e) {
      const value = e && e.target ? e.target.value : e;
      this.obj.paymentDate = parseDateInput(value) || null;
    },
    validate() {
      this.errors = { vendor: "", voucherDate: "", paymentMethod: "", totalAmount: "" };
      if (!this.obj.vendorId) this.errors.vendor = "Choose the vendor you're paying.";
      if (!this.obj.voucherDate) this.errors.voucherDate = "Choose the voucher date.";
      if (!this.obj.paymentMethod) this.errors.paymentMethod = "Choose how you'll pay.";
      if (!this.obj.totalAmount || this.obj.totalAmount <= 0) this.errors.totalAmount = "Enter an amount above RM 0.00, or add bills or expenses below.";
      const first = ["vendor", "voucherDate", "paymentMethod", "totalAmount"].find((k) => this.errors[k]);
      if (!first) return true;
      this.$nextTick(() => {
        const refName = { vendor: "vendorField", voucherDate: "voucherDateField", paymentMethod: "methodField", totalAmount: "amountField" }[first];
        const ref = this.$refs[refName];
        const el = ref && (ref.$el || ref);
        const input = el && el.querySelector && el.querySelector("input, select");
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
        if (input) input.focus();
      });
      return false;
    },
    // Returns true when saved. `navigate: false` keeps the user on the page (used by submit for approval).
    async save({ navigate = true } = {}) {
      if (this.saving) return false;
      if (!this.validate()) return false;

      this.saving = true;
      try {
        // Prepare data for API
        const payload = {
          ...this.obj,
        };

        // Remove id field if it's null (for new records)
        if (!payload.id) {
          delete payload.id;
        }

        if (this.obj.id) {
          await paymentVoucherApi.update(payload);
          this.$toast.success("Payment voucher saved");
        } else {
          const data = await paymentVoucherApi.create(payload);
          this.obj.id = (data && data.result && data.result.id) || (data && data.id);
          this.$toast.success("Payment voucher created");
        }

        if (navigate) this.$router.push({ name: "PaymentVoucherList" });
        return true;
      } catch (error) {
        console.error("Save error:", error);
        this.$toast.error("Couldn't save the payment voucher. " + helper.getErrorMessage(error && error.data, "Please try again."));
        return false;
      } finally {
        this.saving = false;
      }
    },
    async saveAndApprove() {
      const saved = await this.save({ navigate: false });
      if (!saved) return;
      // Change status to PendingApproval
      this.obj.status = 1;
      const changed = await this.changeState({ id: 1, name: "PendingApproval" });
      if (changed) this.$router.push({ name: "PaymentVoucherList" });
    },
    statusActionLabel(status) {
      switch (status.name) {
        case "Draft": return "Move to draft";
        case "PendingApproval": return "Submit for approval";
        case "Approved": return "Approve";
        case "Paid": return "Mark as paid";
        case "Reconciled": return "Mark as reconciled";
        case "Rejected": return "Reject";
        case "Voided": return "Void";
        default: return badgeLabel("paymentVoucher", status.name);
      }
    },
    askStatusChange(status) {
      this.statusConfirm = { show: true, status, busy: false };
    },
    async confirmStatusChange() {
      if (this.statusConfirm.busy || !this.statusConfirm.status) return;
      this.statusConfirm.busy = true;
      try {
        await this.changeState(this.statusConfirm.status);
      } finally {
        this.statusConfirm = { show: false, status: null, busy: false };
      }
    },
    async changeState(status) {
      if (!this.obj.id) {
        this.$toast.warning("Save the voucher before changing its status");
        return false;
      }

      try {
        this.obj.status = status.id;
        await paymentVoucherApi.updateState(this.obj);
        this.$toast.success(`Voucher is now ${badgeLabel("paymentVoucher", status.name).toLowerCase()}`);
        await this.loadVoucher(this.obj.id);
        return true;
      } catch (error) {
        this.$toast.error("Couldn't change the status. " + helper.getErrorMessage(error && error.data, "Please try again."));
        return false;
      }
    },
    addNew() {
      this.$router.push({ name: "PaymentVoucher" });
      this.obj = {
        id: null,
        voucherNumber: "",
        voucherDate: mytToday(),
        paymentDate: null,
        vendorId: null,
        payeeName: "",
        paymentMethod: "",
        paymentReference: "",
        totalAmount: 0,
        description: "",
        notes: "",
        status: 0,
        statusDescription: "Draft",
        items: [],
      };
      this.selectedVendor = null;
      this.generateVoucherNumber();
    },
    cancel() {
      this.$router.push({ name: "PaymentVoucherList" });
    },
    badgeColor,
    badgeLabel,
    formatDate(date) {
      return date ? formatDate(date) : "-";
    },
    formatCurrency(amount) {
      return `RM ${formatMyr(amount)}`;
    },
    getItemTypeLabel(type) {
      const labels = {
        0: "Bill",
        1: "Expense",
        2: "Claim",
        3: "Advance",
        4: "Loan",
        99: "Other",
      };
      return labels[type] || "Unknown";
    },
    getItemTypeColor(type) {
      // Item types are categories, not statuses: one neutral colour, the label carries the meaning.
      return "light";
    },
    generateGuid() {
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
        /[xy]/g,
        function (c) {
          var r = (Math.random() * 16) | 0,
            v = c == "x" ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        }
      );
    },

    async uploadedPaymentSlip(data) {
      if (data.uploadedFiles && data.uploadedFiles.length > 0) {
        this.obj.documentPaymentProofId = data.uploadedFiles[0].id;

        // Save the voucher to persist the document link
        if (this.obj.id) {
          try {
            const payload = { ...this.obj };
            delete payload.items;

            await paymentVoucherApi.update(payload);
            this.$toast.success("Payment slip uploaded");

            // Reload voucher to get updated document details
            await this.loadVoucher(this.obj.id);
          } catch (error) {
            console.error("Failed to save document link:", error);
            this.$toast.error("Couldn't attach the payment slip. Please try again.");
          }
        } else {
          this.$toast.info("Payment slip uploaded. Save the voucher to keep it");
        }
      }
    },
    isPdf(document) {
      try {
        if (document.contentType == "application/pdf") return true;
        return false;
      } catch (error) {
        return false;
      }
    },
    getPaymentSlipUrl() {
      return window.apiUrl + "documents/file/" + this.obj.documentPaymentProofId;
    },
    getPaymentSlipName() {
      if (this.obj.documentPaymentProof == null) return "No payment slip uploaded";
      if (this.obj.documentPaymentProof.fileName == null) return "Unknown document";
      return this.obj.documentPaymentProof.fileName;
    },
    async removePaymentSlip() {
      this.showRemoveSlipModal = false;
      try {
        await paymentVoucherApi.removePaymentProof(this.obj.id, this.obj.documentPaymentProofId);
        this.obj.documentPaymentProofId = null;
        this.obj.documentPaymentProof = null;
        this.$toast.success("Payment slip removed");
        await this.loadVoucher(this.obj.id);
      } catch (error) {
        this.$toast.error("Couldn't remove the payment slip. Please try again.");
      }
    },
  },
};
</script>

<style scoped>
.font-weight-bold {
  font-weight: 600;
}


</style>
